import { Component, OnInit, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { InsuranceApiService, InsuranceProductDetail, InsuranceProductListItem, InsuranceRatingPlan } from './insurance-api.service';

type RatingPlanRow = InsuranceRatingPlan & {
  productId: string | null;
  productCode: string;
  productName: string;
  versionNumber: number | null;
  versionStatus: string;
};

@Component({
  standalone: true,
  selector: 'ain-insurance-rating-plans',
  imports: [AinPageComponent, AinStateComponent, TableModule, ButtonModule, InputTextModule, SelectModule, FormsModule, InfoHintComponent],
  template: `
    <ain-page [title]="t('insurance.rating.title')" [description]="t('insurance.rating.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'insurance.rating.help.page.title'"
        [descriptionKey]="'insurance.rating.help.page.description'"
        [bulletKeys]="[
          'insurance.rating.help.page.bullets.scope',
          'insurance.rating.help.page.bullets.usage',
          'insurance.rating.help.page.bullets.audit'
        ]" />
      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        <section class="card panel">
          <div class="toolbar">
            <button pButton type="button" [label]="t('insurance.rating.actions.newPlan')" (click)="openCreate()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.refresh')" (click)="load()"></button>
            <button pButton type="button" severity="secondary" [label]="t('insurance.actions.open')" [disabled]="selectedRows().length !== 1" (click)="openSelected()"></button>
          </div>

          <details class="filters" [open]="true">
            <summary>
              <span>{{ t('insurance.filters.title') }}</span>
              <ain-info-hint
                [titleKey]="'insurance.rating.help.listFilters.title'"
                [descriptionKey]="'insurance.rating.help.listFilters.description'"
                [bulletKeys]="[
                  'insurance.rating.help.listFilters.bullets.search',
                  'insurance.rating.help.listFilters.bullets.product',
                  'insurance.rating.help.listFilters.bullets.mode'
                ]" />
            </summary>
            <div class="filter-grid">
              <label>
                <span>{{ t('insurance.rating.filters.search') }}</span>
                <input pInputText [value]="search()" (input)="onSearch($event)" />
              </label>
              <label>
                <span>{{ t('insurance.rating.fields.product') }}</span>
                <p-select [options]="productOptions()" optionLabel="label" optionValue="value" [ngModel]="selectedProductId()" (ngModelChange)="onProductFilterChanged($event)" [showClear]="true" appendTo="body" />
              </label>
              <label>
                <span>{{ t('insurance.rating.fields.mode') }}</span>
                <p-select [options]="modeOptions()" optionLabel="label" optionValue="value" [ngModel]="selectedMode()" (ngModelChange)="onModeFilterChanged($event)" [showClear]="true" appendTo="body" />
              </label>
            </div>
          </details>

          @if (filteredRows().length === 0) {
            <ain-state type="empty" [title]="t('insurance.rating.states.empty')" [message]="t('insurance.rating.states.emptyMessage')" />
          } @else {
            <p-table
              [value]="filteredRows()"
              dataKey="id"
              [paginator]="true"
              [rows]="10"
              [selection]="selectedRows()"
              (selectionChange)="onSelectionChange($event)">
              <ng-template pTemplate="header">
                <tr>
                  <th style="width:44px"><p-tableHeaderCheckbox /></th>
                  <th>{{ t('insurance.rating.fields.planCode') }}</th>
                  <th>{{ t('insurance.rating.fields.planName') }}</th>
                  <th>{{ t('insurance.rating.fields.product') }}</th>
                  <th>{{ t('insurance.rating.fields.version') }}</th>
                  <th>{{ t('insurance.rating.fields.mode') }}</th>
                </tr>
              </ng-template>
              <ng-template pTemplate="body" let-row>
                <tr>
                  <td><p-tableCheckbox [value]="row" /></td>
                  <td><button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.code }}</button></td>
                  <td>{{ row.name }}</td>
                  <td>{{ row.productCode }} - {{ row.productName }}</td>
                  <td>{{ row.versionNumber ? ('v' + row.versionNumber + ' (' + row.versionStatus + ')') : '-' }}</td>
                  <td>{{ row.calculationMode }}</td>
                </tr>
              </ng-template>
            </p-table>
          }
        </section>
      }
    </ain-page>
  `,
  styles: [`
    .card{border:1px solid #e5e7eb;border-radius:12px;background:#fff;padding:12px;display:grid;gap:10px}
    .panel{display:grid;gap:10px}
    .toolbar{display:flex;gap:8px;flex-wrap:wrap;align-items:center}
    .filters{border:1px solid #e5e7eb;border-radius:10px;padding:8px 10px;background:#f9fafb}
    .filters summary{display:flex;align-items:center;gap:8px}
    .filter-grid{display:grid;grid-template-columns:repeat(3,minmax(0,1fr));gap:10px;margin-top:8px}
    .filter-grid label{display:grid;gap:6px}
    .link-btn{border:0;background:none;color:#1d4ed8;cursor:pointer;font-weight:600;padding:0}
    @media (max-width: 900px){ .filter-grid{grid-template-columns:1fr} }
  `]
})
export class InsuranceRatingPlansComponent implements OnInit {
  private readonly api = inject(InsuranceApiService);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);

  readonly loading = signal(true);
  readonly rows = signal<RatingPlanRow[]>([]);
  readonly selectedRows = signal<RatingPlanRow[]>([]);
  readonly search = signal('');
  readonly selectedProductId = signal<string | null>(null);
  readonly selectedMode = signal<string | null>(null);

  readonly products = signal<InsuranceProductListItem[]>([]);

  readonly filteredRows = computed(() => {
    const search = this.search().trim().toLowerCase();
    const productId = this.selectedProductId();
    const mode = this.selectedMode();
    return this.rows().filter(row => {
      if (productId && row.productId !== productId) {
        return false;
      }
      if (mode && row.calculationMode !== mode) {
        return false;
      }
      if (!search) {
        return true;
      }
      return row.code.toLowerCase().includes(search)
        || row.name.toLowerCase().includes(search)
        || row.productCode.toLowerCase().includes(search)
        || row.productName.toLowerCase().includes(search);
    });
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading.set(true);
    this.api.listProducts().subscribe({
      next: products => {
        this.products.set(products);
        this.api.listRatingPlans().subscribe({
          next: plans => {
            this.buildRows(plans, products, rows => {
              this.rows.set(rows);
              this.loading.set(false);
            });
          },
          error: () => {
            this.rows.set([]);
            this.loading.set(false);
          }
        });
      },
      error: () => {
        this.products.set([]);
        this.rows.set([]);
        this.loading.set(false);
      }
    });
  }

  productOptions(): Array<{ label: string; value: string }> {
    return this.products().map(item => ({ value: item.id, label: `${item.code} - ${item.name}` }));
  }

  modeOptions(): Array<{ label: string; value: string }> {
    const modes = Array.from(new Set(this.rows().map(item => item.calculationMode)));
    return modes.map(mode => ({ value: mode, label: mode }));
  }

  onSearch(event: Event): void {
    this.search.set((event.target as HTMLInputElement).value ?? '');
  }

  onSelectionChange(selection: RatingPlanRow[] | RatingPlanRow | null): void {
    this.selectedRows.set(Array.isArray(selection) ? selection : selection ? [selection] : []);
  }

  onProductFilterChanged(value: string | null): void {
    this.selectedProductId.set(value);
  }

  onModeFilterChanged(value: string | null): void {
    this.selectedMode.set(value);
  }

  openSelected(): void {
    const row = this.selectedRows()[0];
    if (row) {
      this.openDetail(row.id);
    }
  }

  openDetail(id: string): void {
    void this.router.navigate(['/insurance/rating', id]);
  }

  openCreate(): void {
    void this.router.navigate(['/insurance/rating/new']);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private buildRows(
    plans: InsuranceRatingPlan[],
    products: InsuranceProductListItem[],
    onDone: (rows: RatingPlanRow[]) => void
  ): void {
    if (products.length === 0) {
      onDone(this.mapRows(plans, new Map<string, { product: InsuranceProductListItem; versionNumber: number | null; versionStatus: string }>()))
      return;
    }

    let pending = products.length;
    const versionIndex = new Map<string, { product: InsuranceProductListItem; versionNumber: number | null; versionStatus: string }>();
    products.forEach(product => {
      this.api.getProduct(product.id).subscribe({
        next: detail => this.indexProductVersions(product, detail, versionIndex),
        error: () => {},
        complete: () => {
          pending -= 1;
          if (pending === 0) {
            onDone(this.mapRows(plans, versionIndex));
          }
        }
      });
    });
  }

  private indexProductVersions(
    product: InsuranceProductListItem,
    detail: InsuranceProductDetail | null,
    versionIndex: Map<string, { product: InsuranceProductListItem; versionNumber: number | null; versionStatus: string }>
  ): void {
    const versions = detail?.versions ?? [];
    versions.forEach(version => {
      versionIndex.set(version.id, {
        product,
        versionNumber: version.versionNumber,
        versionStatus: version.status
      });
    });
  }

  private mapRows(
    plans: InsuranceRatingPlan[],
    versionIndex: Map<string, { product: InsuranceProductListItem; versionNumber: number | null; versionStatus: string }>
  ): RatingPlanRow[] {
    return plans.map(plan => {
      const version = versionIndex.get(plan.productVersionId);
      const product = version?.product ?? null;
      return {
        ...plan,
        productId: product?.id ?? null,
        productCode: product?.code ?? '-',
        productName: product?.name ?? '-',
        versionNumber: version?.versionNumber ?? null,
        versionStatus: version?.versionStatus ?? '-'
      };
    });
  }
}
